import {
  BackComponent,
  ButtonLink,
  CartCard,
  CheckoutPriceCard,
  Column,
  Loader,
  ShowWhen,
  TextWithLink,
  Typography,
} from '@/components'
import { ComponentColors, ThemeColor, Categories } from '@/enums'
import { useContactModal } from '@/providers'
import { paths } from '@/routes/paths'
import { Redirect } from '@/routes/Redirect'
import { useLocation } from '@/services'
import {
  mapWithKey,
  toPixels,
  useGoBack,
  useGoToPath,
  useIsMobile,
} from '@/utils'
import { UserTypes } from '@/types/user-profile'

import { Button } from '@surein/ui'
import { ArrowRight } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useOutletContext, useParams } from 'react-router-dom'
import {
  CartContent,
  CheckoutStepperActions,
  ContactButtonImage,
  Container,
  StyledWidgetContent,
} from './styles'
interface ProductData {
  grossPrice: number
  originalGrossPrice?: number
  paymentPeriod: 'monthly' | 'yearly'
  insuranceProductId: string
  carrierData: {
    logoUrl: string
    name: string
  }
  categoryId: keyof typeof Categories
  recommendationProductId: string
}

interface OutletContext {
  cart: Record<string, ProductData[]>
  user: {
    companies: Array<{
      companyId: string
      userType: UserTypes
    }>
  }
}

const useMessages = () => {
  const { t } = useTranslation()

  return {
    title: t('navigation.orders'),
    emptyCart: t('emptyCart'),
    location: t('location'),
    insurance: t('common.insurance'),
    insurances: t('insurances'),
    totalPrice: t('totalPrice'),
    next: t('next'),
  }
}

const ContactButtonIcon = () => (
  <ContactButtonImage name="chatblock" type="png" />
)

const EmptyCart = () => {
  const messages = useMessages()
  const { locationId } = useParams<{ locationId: string }>()

  const goToFindInsurances = useGoToPath(
    paths.findInsurancesWithKey(locationId || ''),
  )

  return (
    <TextWithLink>
      <Trans
        components={[
          <ButtonLink
            color={ComponentColors.primary}
            onClick={goToFindInsurances}
          />,
        ]}
        i18nKey={messages.emptyCart}
      />
    </TextWithLink>
  )
}

export const CartPage = () => {
  const widgetRef = useRef<HTMLDivElement>(null)
  const { isMobile } = useIsMobile()
  const messages = useMessages()
  const { locationId } = useParams<{ locationId: string }>()
  const { data: locationData, isLoading: isLocationDataLoading } = useLocation(
    locationId || '',
  )
  const goToCheckout = useGoToPath(paths.viewCheckoutWithKey(locationId || ''))
  const { cart, user } = useOutletContext<OutletContext>()
  const { t } = useTranslation()
  const { openContact } = useContactModal()
  const goBack = useGoBack()

  const data = cart[locationId || ''] || []

  useEffect(() => {
    if (isMobile && widgetRef.current) {
      const { offsetHeight } = widgetRef.current.children[0] as HTMLElement

      widgetRef.current.style.minHeight = toPixels(offsetHeight)
    }
  }, [isMobile, widgetRef.current])

  if (isLocationDataLoading) return <Loader />

  const permissions = user?.companies?.find(
    (company) => company.companyId === locationData?.companyId,
  )?.userType

  const canAccessCart = permissions?.MD || permissions?.owner

  if (locationData && !canAccessCart)
    return <Redirect to={paths.authRedirect} />

  return (
    <>
      <Container>
        <BackComponent onBack={goBack} />
        <Column justifyContent="space-between" gap="16px" flex={1}>
          <CartContent>
            <Typography variant="h3">{messages.title}</Typography>
            {locationData?.name && (
              <Typography
                bold
                color={ThemeColor.b50}
                singleLine
                variant="inputDescription"
              >
                {locationData.name}
              </Typography>
            )}
            <ShowWhen fallback={EmptyCart} when={data.length > 0}>
              {mapWithKey(CartCard, 'recommendationProductId', data)}
            </ShowWhen>
          </CartContent>
          <CheckoutStepperActions>
            <ShowWhen when={!isMobile}>
              <Button
                icon={ContactButtonIcon}
                onClick={openContact}
                variant="outline"
              >
                {t('common.contactUs')}
              </Button>
              <Button
                variant="primary"
                shape="square"
                disabled={!data.length}
                onClick={() => goToCheckout()}
                suffixIcon={ArrowRight}
              >
                {t('next')}
              </Button>
            </ShowWhen>
            <ShowWhen when={isMobile}>
              <Button
                className="w-full"
                onClick={() => goToCheckout()}
                reverted
                variant="primary"
                shape="square"
                suffixIcon={ArrowRight}
              >
                {t('next')}
              </Button>
            </ShowWhen>
          </CheckoutStepperActions>
        </Column>
      </Container>
      <ShowWhen when={data.length > 0}>
        <StyledWidgetContent ref={widgetRef}>
          <CheckoutPriceCard buttonText={messages.next} data={data} />
        </StyledWidgetContent>
      </ShowWhen>
    </>
  )
}
